import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { HiArrowUpRight } from 'react-icons/hi2';
import { SplitWords } from './SplitWords';
import { EASE } from '../lib/motion';
import { sceneState, type ServiceKey } from '../three/state';

const keys: ServiceKey[] = ['webapps', 'eshops', 'games'];

/**
 * The 3D core sits on the left for this section. Hovering a service (or
 * scrolling it to the middle of the screen on touch) morphs the core's surface
 * into that service's character: calm for web apps, precise for e-shops,
 * restless for interactive work.
 */
export function Services() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const [active, setActive] = useState<ServiceKey>('webapps');

  useEffect(() => {
    sceneState.service = active;
  }, [active]);

  return (
    <section id="services" className="relative px-5 py-28 md:px-8 md:py-44">
      <div className="mx-auto max-w-[1400px] lg:grid lg:grid-cols-12">
        <div className="lg:col-span-7 lg:col-start-6">
          <h2 className="font-display text-5xl font-semibold leading-[1] tracking-[-0.04em] md:text-7xl">
            <SplitWords text={t('services.title')} />
          </h2>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mt-6 max-w-[48ch] text-lg text-muted"
          >
            {t('services.lead')}
          </motion.p>

          <ul className="mt-16 md:mt-24">
            {keys.map((key, i) => {
              const tags = t(`services.items.${key}.tags`, {
                returnObjects: true,
              }) as string[];
              const isActive = active === key;
              return (
                <motion.li
                  key={key}
                  onPointerEnter={() => setActive(key)}
                  onViewportEnter={() => setActive(key)}
                  viewport={{ margin: '-48% 0px -48% 0px' }}
                  className="group relative border-t border-line-strong"
                >
                  {/* Hairline that charges up in the accent for the active row. */}
                  <span
                    aria-hidden
                    className={`absolute inset-x-0 -top-px h-px origin-left bg-accent transition-transform duration-700 ease-out-expo ${
                      isActive ? 'scale-x-100' : 'scale-x-0'
                    }`}
                  />
                  <motion.div
                    initial={reduce ? false : { opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
                    className="flex items-start gap-5 py-9 md:gap-10 md:py-12"
                  >
                    <span
                      className={`mt-3 font-mono text-sm transition-colors duration-500 ${
                        isActive ? 'text-accent' : 'text-faint'
                      }`}
                    >
                      {t(`services.items.${key}.index`)}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3
                          className={`font-display text-3xl font-semibold tracking-[-0.03em] transition-all duration-700 ease-out-expo md:text-5xl ${
                            isActive
                              ? 'translate-x-2 text-fg md:translate-x-4'
                              : 'text-fg/45'
                          }`}
                        >
                          {t(`services.items.${key}.title`)}
                        </h3>
                        <HiArrowUpRight
                          className={`shrink-0 text-2xl transition-all duration-500 md:text-3xl ${
                            isActive
                              ? 'rotate-45 text-accent opacity-100'
                              : 'text-faint opacity-40'
                          }`}
                        />
                      </div>
                      <div
                        className={`transition-opacity duration-700 ${
                          isActive ? 'opacity-100' : 'opacity-50'
                        }`}
                      >
                        <p className="mt-4 max-w-[52ch] text-[15px] leading-relaxed text-muted md:ml-4">
                          {t(`services.items.${key}.body`)}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2 md:ml-4">
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-line-strong bg-base/40 px-3 py-1 font-mono text-xs text-muted backdrop-blur-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
